import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faUserHelmetSafety, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import Helper from "../Helper";
import { CommandMessage, CommandProjectInfo } from "./CommandTypes";

interface FullscreenJobsiteAlertProps {
    project: CommandProjectInfo;
    messages: CommandMessage[];
}

const FullscreenJobsiteAlert: React.FC<FullscreenJobsiteAlertProps> = ({ project, messages }) => {
    const alertDurationSeconds = useMemo(
        () => Math.max(project.jobsite_alert_display_time * 60, 0),
        [project.jobsite_alert_display_time]
    );
    const [alertMessage, setAlertMessage] = React.useState<CommandMessage | null>(null);
    const [showMessage, setShowMessage] = React.useState<boolean>(false);
    const [countdown, setCountdown] = React.useState<number>(alertDurationSeconds);
    const lastDisplayedAlertId = useRef<number | null>(null);

    if (!project) {
        return null;
    }

    const handleDismiss = useCallback(() => {
        setShowMessage(false);
    }, []);

    const createdAt = useMemo(() => {
        if (!alertMessage?.created_at) {
            return null;
        }

        return dayjs(alertMessage.created_at).tz(project.timezone);
    }, [alertMessage?.created_at, project.timezone]);

    const dateLabel = useMemo(() => {
        return createdAt ? Helper.notificationsDateStringFormatter(createdAt) : "";
    }, [createdAt]);

    const timeLabel = useMemo(() => {
        return createdAt ? createdAt.format("LTS") : "";
    }, [createdAt]);

    const dismissingMessage = useMemo(() => {
        if (countdown >= 60) {
            const minutes = Math.floor(countdown / 60);
            return `${minutes} minute${minutes === 1 ? "" : "s"}`;
        }

        return `${countdown} second${countdown === 1 ? "" : "s"}`;
    }, [countdown]);

    useEffect(() => {
        if (messages && messages.length > 0) {
            const now = dayjs();
            const alert = messages.find(
                (message) =>
                    message.message_type === "alert" &&
                    now.diff(dayjs(message.created_at), "second") <= 30 &&
                    message.id !== lastDisplayedAlertId.current
            );

            if (alert) {
                lastDisplayedAlertId.current = alert.id;
                setAlertMessage(alert);
                setShowMessage(true);
                setCountdown(alertDurationSeconds);
            }
        }
    }, [messages, alertDurationSeconds]);

    useEffect(() => {
        if (!showMessage || countdown <= 0) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            setCountdown((previous) => previous - 1);
        }, 1000);

        return () => window.clearTimeout(timeoutId);
    }, [showMessage, countdown]);

    useEffect(() => {
        if (showMessage && countdown <= 0) {
            setShowMessage(false);
        }
    }, [showMessage, countdown]);

    return (
        <div
            className={`absolute top-0 left-0 w-full flowing-gradient flex items-center justify-center z-99999 transition-all duration-700 overflow-hidden ${
                showMessage ? "h-full" : "h-0"
            }`}
        >
            <div className="absolute top-8 right-8 flex flex-row items-center gap-4">
                <div className="flex flex-col items-end gap-2">
                    <span className="text-neutral-900/50 hover:text-neutral-900 transition-colors text-xl">
                        Dismissing in {dismissingMessage}...
                    </span>
                </div>

                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label="Dismiss alert"
                    className="w-14 h-14 rounded-full flex items-center justify-center text-neutral-900/50 hover:text-neutral-900 hover:bg-black/10 transition-colors cursor-pointer"
                >
                    <FontAwesomeIcon icon={faXmark as IconProp} className="text-3xl" />
                </button>
            </div>

            <div
                className={`flex flex-col items-center gap-8 max-w-6xl px-16 transition-all duration-250 ${
                    alertMessage ? "scale-100" : "scale-95"
                }`}
            >
                <p className="text-6xl md:text-7xl xl:text-8xl text-center text-white font-semibold leading-tight wrap-break-word">
                    {alertMessage?.body}
                </p>

                <div className="flex flex-row items-center justify-center gap-4 text-white/90 mt-2">
                    {alertMessage?.sender?.avatar_url ? (
                        <img
                            src={alertMessage?.sender?.avatar_url}
                            alt={alertMessage?.sender?.fullname || "Sender"}
                            className="w-48 h-48 rounded-full object-cover ring-2 ring-white/40"
                        />
                    ) : (
                        <div className="w-48 h-48 rounded-full bg-neutral-900/20 ring-2 ring-white/40 flex items-center justify-center">
                            <FontAwesomeIcon icon={faUserHelmetSafety as IconProp} className="text-xl text-white" />
                        </div>
                    )}
                    <div className="flex flex-col items-start">
                        <span className="text-3xl text-white/70">Jobsite Alert</span>
                        <span className="text-4xl font-medium">
                            {alertMessage?.sender?.fullname || "Unknown Sender"}
                        </span>

                        <span className="text-2xl text-white/70">
                            {"Sent at: "}
                            {dateLabel}
                            {timeLabel}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FullscreenJobsiteAlert;
